'use client';

import { useSyncExternalStore } from 'react';
import { releaseBestBuyHold } from './api';
import { BB_DATE_RE, BB_PICKUP_HOLD_STORAGE_KEY } from './config';

/** The hold this tab is carrying. Kept in sessionStorage so a reload or a
 * return from checkout can reuse it until it expires. */
export type StoredHold = {
  order_reference: string;
  /** ISO instant. */
  expires_at: string;
  location_id: string;
  sku: string;
  /** Best Buy's promise date for the held unit (YYYY-MM-DD). */
  promise_date?: string;
  /** The cart the hold was stamped on. A paid checkout ends that cart, so a
   * hold whose order already claimed it is never reused for a new cart. */
  cart_id?: string;
};

const REFERENCE_RE = /^UHBBY-[A-Z0-9-]{1,40}$/i;

const parseStored = (raw: string | null): StoredHold | null => {
  if (!raw) return null;
  try {
    const h = JSON.parse(raw) as Partial<StoredHold>;
    if (
      typeof h.order_reference === 'string' &&
      REFERENCE_RE.test(h.order_reference) &&
      typeof h.expires_at === 'string' &&
      !Number.isNaN(new Date(h.expires_at).getTime()) &&
      typeof h.location_id === 'string' &&
      typeof h.sku === 'string'
    ) {
      return {
        order_reference: h.order_reference,
        expires_at: h.expires_at,
        location_id: h.location_id,
        sku: h.sku,
        ...(typeof h.promise_date === 'string' &&
        BB_DATE_RE.test(h.promise_date)
          ? { promise_date: h.promise_date }
          : {}),
        ...(typeof h.cart_id === 'string' ? { cart_id: h.cart_id } : {}),
      };
    }
  } catch {}
  return null;
};

let cached: StoredHold | null = null;
let loaded = false;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((fn) => fn());

export const readStoredHold = (): StoredHold | null => {
  if (!loaded && typeof window !== 'undefined') {
    loaded = true;
    try {
      cached = parseStored(sessionStorage.getItem(BB_PICKUP_HOLD_STORAGE_KEY));
    } catch {
      cached = null;
    }
  }
  return cached;
};

export const writeStoredHold = (hold: StoredHold): void => {
  loaded = true;
  cached = hold;
  try {
    sessionStorage.setItem(BB_PICKUP_HOLD_STORAGE_KEY, JSON.stringify(hold));
  } catch {}
  notify();
};

export const clearStoredHold = (): void => {
  loaded = true;
  if (cached === null) return;
  cached = null;
  try {
    sessionStorage.removeItem(BB_PICKUP_HOLD_STORAGE_KEY);
  } catch {}
  notify();
};

export const holdExpiresInMs = (hold: StoredHold, now = Date.now()): number =>
  new Date(hold.expires_at).getTime() - now;

/** Smallest remaining time worth reusing a hold for: checkout still has to
 * load and the shopper to pay. */
export const HOLD_REUSE_MIN_MS = 60_000;

/** Clears the stored hold and tells the backend to free it. The DELETE is
 * best effort: UI never waits on it, a failure just means the hold runs out
 * on its own. */
// The live cart's id, kept current by useBestBuyPickupCart.
let liveCartId: string | null = null;
export const setLiveCartId = (id: string | null): void => {
  liveCartId = id;
};

/** Frees the stored hold. Only a hold made for the live cart is released on
 * the backend: one from an earlier cart may already belong to a paid order,
 * so it is only forgotten. */
export const releaseStoredHold = (): void => {
  const hold = readStoredHold();
  if (!hold) return;
  clearStoredHold();
  if (hold.cart_id && hold.cart_id !== liveCartId) return;
  void releaseBestBuyHold(hold.order_reference).catch(() => undefined);
};

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

export const useStoredHold = (): StoredHold | null =>
  useSyncExternalStore(subscribe, readStoredHold, () => null);
